import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { urlHashDatePicker } from 'constants/url'
import { EmptyObject } from 'lib/object/objectUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import { buildSearchParamsFromFilters } from 'lib/search/searchUtils'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { OfferUnavailableReason } from 'lib/search/constants'
import { useHighlightMessages } from 'components/OfferList/OfferListTiles/constants'
import { dateDifference } from 'lib/datetime/dateUtils'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BundleOffer | App.VillaOffer | App.VillaOfferSummary
  unavailableReason: string | undefined
  filters?: App.OfferListFilters
}

function HotelSearchTileUnavailabilityBanner({
  offer,
  unavailableReason,
  filters = EmptyObject,
}: Props) {
  const intl = useIntl()
  const { unavailableBannerMessageStayMinNights } = useHighlightMessages()

  const soldOutLabel = intl.formatMessage({
    id: 'zsOD4NQQJd',
    defaultMessage: 'Sold out for your dates',
    description: 'Banner message when offer is sold out for the selected dates',
  })
  const flightClassUnavailableLabel = intl.formatMessage({
    id: 'RNqCIrbNxG',
    defaultMessage: 'Selected flight class is not available for your dates',
    description: 'Banner message when selected flight class is unavailable',
  })
  const fareClassUnavailableLabel = intl.formatMessage({
    id: 'v0rqHXZViz',
    defaultMessage: 'Unavailable for your fare class',
    description: 'Banner message when offer is unavailable for selected fare class',
  })
  const capacityExceededLabel = intl.formatMessage({
    id: 'zP3a8FGEHd',
    defaultMessage: 'Unavailable for the number of guests selected',
    description: 'Banner message when guest count exceeds capacity in the search interface',
  })
  const filtersUnavailableLabel = intl.formatMessage({
    id: 'yUzcHAa57J',
    defaultMessage: 'Unavailable for your filters',
    description: 'Banner message when offer is unavailable for selected filters',
  })
  const datesUnavailableLabel = intl.formatMessage({
    id: 'NctZyv9Xvu',
    defaultMessage: 'Unavailable for your dates',
    description: 'Default banner message when offer is unavailable for selected dates',
  })
  const viewOfferLabel = intl.formatMessage({
    id: 'T3tE7/3hZN',
    defaultMessage: 'View offer',
    description: 'CTA button to view offer details from unavailability banner',
  })
  const viewAvailableDatesLabel = intl.formatMessage({
    id: 'OgfILUEp6q',
    defaultMessage: 'View available dates',
    description: 'CTA button to view available dates from unavailability banner',
  })

  const checkIn = filters?.checkIn
  const checkOut = filters?.checkOut
  const duration = useMemo(() => {
    if (checkIn && checkOut) {
      return dateDifference(new Date(checkOut), new Date(checkIn)).days
    }
    return undefined
  }, [checkIn, checkOut])

  const bannerMessage = useMemo(() => {
    if (offer.isSoldOut) return soldOutLabel
    if (unavailableReason === OfferUnavailableReason.FLIGHT_CLASS_UNAVAILABLE) {
      return flightClassUnavailableLabel
    }
    if (unavailableReason === OfferUnavailableReason.FARE_CLASS_UNAVAILABLE) {
      return fareClassUnavailableLabel
    }
    if (unavailableReason === OfferUnavailableReason.CAPACITY_EXCEEDED) {
      return capacityExceededLabel
    }
    if (unavailableReason === OfferUnavailableReason.UNAVAILABLE_FOR_FILTERS_SELECTED) {
      return filtersUnavailableLabel
    }

    if (duration &&
      offer.minDuration &&
      offer.minDuration > duration) {
      return unavailableBannerMessageStayMinNights(offer.minDuration)
    }

    return datesUnavailableLabel
  }, [capacityExceededLabel, datesUnavailableLabel, duration, fareClassUnavailableLabel, filtersUnavailableLabel, flightClassUnavailableLabel, offer.isSoldOut, offer.minDuration, soldOutLabel, unavailableBannerMessageStayMinNights, unavailableReason])

  const offerUrl = useMemo(() => {
    const searchParams = buildSearchParamsFromFilters({
      ...filters,
      checkIn: undefined,
      checkOut: undefined,
    })

    if (
      unavailableReason === OfferUnavailableReason.CAPACITY_EXCEEDED ||
      unavailableReason === OfferUnavailableReason.UNAVAILABLE_FOR_FILTERS_SELECTED ||
      unavailableReason === OfferUnavailableReason.FARE_CLASS_UNAVAILABLE
    ) {
      return offerPageURL(offer, searchParams)
    }

    return offerPageURL(offer, searchParams) + urlHashDatePicker
  }, [filters, offer, unavailableReason])
  const primaryActionLabel = useMemo(() => {
    if (unavailableReason === OfferUnavailableReason.CAPACITY_EXCEEDED ||
      unavailableReason === OfferUnavailableReason.UNAVAILABLE_FOR_FILTERS_SELECTED ||
      unavailableReason === OfferUnavailableReason.FARE_CLASS_UNAVAILABLE) {
      return viewOfferLabel
    }
    return viewAvailableDatesLabel
  }, [unavailableReason, viewAvailableDatesLabel, viewOfferLabel])

  const handlePrimaryActionClick = useCallback((event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    event.preventDefault()
    event.stopPropagation()
    window.open(offerUrl, '_blank', 'noopener')
  }, [offerUrl])

  const primaryActionButtonProps = useMemo(() => ({
    children: primaryActionLabel,
    onClick: handlePrimaryActionClick,
  }), [handlePrimaryActionClick, primaryActionLabel])

  return (
    <MessageBanner
      kind="informational-primary"
      primaryActionButtonProps={primaryActionButtonProps}
    >
      <b>{bannerMessage}</b>
    </MessageBanner>
  )
}

export default HotelSearchTileUnavailabilityBanner
